import { useState } from 'react';
import { Button, Spinner } from '@tutero/ui';

import {
  completeSecondFactor,
  sendSecondFactorCode,
  toAuthError,
  type SecondFactorChallenge,
} from '@/lib/auth';

import { AuthErrorText, AuthField } from './AuthField';

type Kind = 'totp' | 'text' | 'backup';

/**
 * The second factor, completed here rather than on a route of its own
 * (`staff-sign-in-here` D4): the code from the staff member's authenticator, the one
 * texted to their verified number, or a backup code. An account with both is asked for
 * the authenticator first, with the text one press away.
 *
 * It resolves nothing itself, for the reason `LoginForm` navigates nothing (D9): the
 * verified code issues the session, and the shell reads it again. A wrong code keeps the
 * step open — a mistyped digit is one keystroke from right.
 */
export interface MfaStepProps {
  challenge: SecondFactorChallenge;
  /** Ask the shell to read the session again, once the factor is accepted. */
  onVerified: () => void;
}

export function MfaStep({ challenge, onVerified }: MfaStepProps) {
  const hasAuthenticator =
    challenge.methods.includes('totp') || challenge.methods.length === 0;
  const hasText = challenge.methods.includes('otp');
  /* A text-only account's code went out with the password (`signInWithEmail`). */
  const [kind, setKind] = useState<Kind>(() =>
    hasAuthenticator ? 'totp' : hasText ? 'text' : 'backup',
  );
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(challenge.refused ?? null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  function show(next: Kind) {
    setKind(next);
    setCode('');
    setError(null);
    setSent(false);
  }

  /** Text a code, then ask for it; a refusal says in the Worker's own words when to retry. */
  async function text() {
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      await sendSecondFactorCode();
      setKind('text');
      setCode('');
      setSent(true);
    } catch (err) {
      setError(toAuthError(err).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completeSecondFactor(code.trim(), kind);
      onVerified();
    } catch (err) {
      setError(toAuthError(err).message);
    } finally {
      /* Also on success: a session read that still answers `null` must leave a live step. */
      setBusy(false);
    }
  }

  const totp = kind === 'totp';
  const digits = kind !== 'backup';
  return (
    <form className="flex w-full max-w-sm flex-col gap-4" onSubmit={submit}>
      <h1 className="text-headline-4">Two-factor authentication</h1>
      <p className="text-regular-m text-left">
        {totp
          ? 'Enter the 6-digit code from your authenticator app.'
          : kind === 'text'
            ? 'Enter the 6-digit code we texted to your phone.'
            : 'Enter one of the backup codes you saved when you set up two-factor authentication.'}
      </p>
      <AuthField
        label={digits ? 'Verification code' : 'Backup code'}
        inputMode={digits ? 'numeric' : 'text'}
        autoComplete="one-time-code"
        {...(digits ? { pattern: '[0-9]{6}', maxLength: 6 } : {})}
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
      <AuthErrorText>{error}</AuthErrorText>
      {sent ? (
        <p role="status" className="text-regular-s text-left">
          We texted you a code.
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={busy || code.trim().length < (digits ? 6 : 1)}
        aria-busy={busy}
      >
        {busy ? <Spinner /> : null}
        Verify
      </Button>
      {kind === 'text' ? (
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => void text()}
        >
          Send Again
        </Button>
      ) : null}
      {/* An authenticator's enrolment hands out backup codes with the secret; a texted one has none. */}
      {hasAuthenticator ? (
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => show(totp ? 'backup' : 'totp')}
        >
          {totp ? 'Use a Backup Code Instead' : 'Use My Authenticator Instead'}
        </Button>
      ) : null}
      {totp && hasText ? (
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => void text()}
        >
          Text Me a Code Instead
        </Button>
      ) : null}
    </form>
  );
}
